import type { FunctionReference } from "convex/server";
import { isConvexAuthError } from "./auth";
import type { ConvexAuthRecoveryResult, ConvexVueClientLike } from "./types";

const DEFAULT_MAX_RECOVERY_ATTEMPTS = 1;

export type RecoverConvexAuth = (context: {
  operation: string;
  error: unknown;
  functionName?: string;
  recoveryAttempt: number;
}) => Promise<ConvexAuthRecoveryResult>;

export interface RunConvexWithAuthRecoveryOptions<T> {
  operation: string;
  functionName?: string;
  run: () => Promise<T>;
  recoverAuth: RecoverConvexAuth;
  maxRecoveryAttempts?: number;
  onError?: (error: unknown, context: { recoveryAttempt: number; isRecoverable: boolean }) => void;
  onUnrecovered?: (error: unknown, result?: ConvexAuthRecoveryResult) => void;
}

export async function runConvexWithAuthRecovery<T>({
  operation,
  functionName,
  run,
  recoverAuth,
  maxRecoveryAttempts,
  onError,
  onUnrecovered,
}: RunConvexWithAuthRecoveryOptions<T>): Promise<T> {
  const recoveryLimit = maxRecoveryAttempts ?? DEFAULT_MAX_RECOVERY_ATTEMPTS;
  let recoveryAttempt = 0;

  while (true) {
    try {
      return await run();
    } catch (error) {
      const isRecoverable = isConvexAuthError(error) && recoveryAttempt < recoveryLimit;
      onError?.(error, { recoveryAttempt, isRecoverable });

      if (!isRecoverable) {
        onUnrecovered?.(error);
        throw error;
      }

      const recovery = await recoverAuth({
        operation,
        error,
        functionName,
        recoveryAttempt,
      });

      if (!recovery.didRecover) {
        onUnrecovered?.(error, recovery);
        throw error;
      }

      recoveryAttempt += 1;
    }
  }
}

export interface RecoverConvexSubscriptionOptions {
  operation: string;
  functionName?: string;
  error: unknown;
  recoveryAttempt: number;
  recoverAuth: RecoverConvexAuth;
  maxRecoveryAttempts?: number;
}

export async function recoverConvexSubscription({
  operation,
  functionName,
  error,
  recoveryAttempt,
  recoverAuth,
  maxRecoveryAttempts,
}: RecoverConvexSubscriptionOptions): Promise<{
  didRecover: boolean;
  nextRecoveryAttempt: number;
  recovery?: ConvexAuthRecoveryResult;
}> {
  const recoveryLimit = maxRecoveryAttempts ?? DEFAULT_MAX_RECOVERY_ATTEMPTS;

  if (!isConvexAuthError(error) || recoveryAttempt >= recoveryLimit) {
    return { didRecover: false, nextRecoveryAttempt: recoveryAttempt };
  }

  const recovery = await recoverAuth({
    operation,
    error,
    functionName,
    recoveryAttempt,
  });

  return {
    didRecover: recovery.didRecover,
    nextRecoveryAttempt: recoveryAttempt + 1,
    recovery,
  };
}

export function createConvexMutationRunner(client: ConvexVueClientLike, recoverAuth: RecoverConvexAuth) {
  return function runMutation<Mutation extends FunctionReference<"mutation">>(
    mutation: Mutation,
    args: Mutation["_args"],
    options: Omit<RunConvexWithAuthRecoveryOptions<Mutation["_returnType"]>, "run" | "recoverAuth">,
  ): Promise<Mutation["_returnType"]> {
    return runConvexWithAuthRecovery({
      ...options,
      recoverAuth,
      run: () => client.mutation(mutation, args),
    });
  };
}

export function createConvexQueryRunner(client: ConvexVueClientLike, recoverAuth: RecoverConvexAuth) {
  return function runQuery<Query extends FunctionReference<"query">>(
    query: Query,
    args: Query["_args"],
    options: Omit<RunConvexWithAuthRecoveryOptions<Query["_returnType"]>, "run" | "recoverAuth">,
  ): Promise<Query["_returnType"]> {
    return runConvexWithAuthRecovery({
      ...options,
      recoverAuth,
      run: () => client.query(query, args),
    });
  };
}
